import * as Dialog from "@radix-ui/react-dialog";
import { RocketIcon, ChevronRightIcon, ChevronLeftIcon } from "@radix-ui/react-icons";
import { useTheme } from "../Theme/ThemeProvider";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMsal } from "@azure/msal-react";
import {
  startTutorial,
  skipTutorial,
  goToStep,
  CHAT_ONLY_STEP_IDS,
} from "../../store/tutorialSlice";
import { markTutorialAsCompleted } from "../../utils/tutorialStorage";
import { createUserPreferencesService } from "../../utils/userPreferences.service";
import type { RootState } from "../../store/store";
import logger from "../../utils/logger";

export interface WelcomePopupProps {
  isOpen: boolean;
  currentPage?: 'main' | 'chat';
  isPromptLibraryOpen?: boolean;
  statisticsPanelOpen?: boolean;
}

export function WelcomePopup({ isOpen, currentPage = 'main', isPromptLibraryOpen = false, statisticsPanelOpen = false }: WelcomePopupProps) {
  const { mode, accent } = useTheme();
  const dispatch = useDispatch();
  const { instance: msalInstance } = useMsal();
  const { steps } = useSelector((state: RootState) => state.tutorial);
  const chatMode = useSelector((state: RootState) => state.chatMode.mode);
  const [currentScreen, setCurrentScreen] = useState(1); // 1 or 2
  const [isMobile, setIsMobile] = useState(false);

  const markAsViewed = useCallback(() => {
    createUserPreferencesService(msalInstance)
      .updateUserPreferences({ guidedTutorial: true })
      .then(() => logger.log('[UserPreferences] guidedTutorial saved successfully'))
      .catch(err => console.error('[UserPreferences] Failed to save guidedTutorial:', err));
  }, [msalInstance]);

  // Fire markAsViewed whenever the popup closes, regardless of how it was closed
  const prevIsOpenRef = useRef(isOpen);
  useEffect(() => {
    if (prevIsOpenRef.current && !isOpen) {
      markAsViewed();
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, markAsViewed]);

  // First filtered step index — needed to jump to correct step on tour start
  const firstFilteredStepIndex = useMemo(() => {
    if (chatMode === '3M Knowledge') {
      const first = steps.find(s => !CHAT_ONLY_STEP_IDS.has(s.id));
      return first ? steps.indexOf(first) : 0;
    }
    return 0;
  }, [steps, chatMode]);

  const onStartTour = useCallback(() => {
    dispatch(startTutorial());
    if (firstFilteredStepIndex !== 0) {
      dispatch(goToStep(firstFilteredStepIndex));
    }
  }, [dispatch, firstFilteredStepIndex]);

  const onSkip = useCallback(() => {
    dispatch(skipTutorial());
    markTutorialAsCompleted();
  }, [dispatch]);
  
  // Detect mobile on mount and window resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Reset to screen 1 when popup opens
  useEffect(() => {
    if (isOpen) {
      setCurrentScreen(1);
    }
  }, [isOpen]);
  
  // Get accent color for styling
  useEffect(() => {
    if (isOpen) {
      setCurrentScreen(1);
    }
  }, [isOpen]);
  
  // Get accent color for styling
  const getAccentColor = (accentColor: string) => {
    if (accentColor === 'purple') return mode === 'dark' ? '#a78bfa' : '#8b5cf6';
    if (accentColor === 'blue') return mode === 'dark' ? '#60a5fa' : '#3b82f6';
    if (accentColor === 'green') return mode === 'dark' ? '#34d399' : '#10b981';
    if (accentColor === 'gold') return mode === 'dark' ? '#fbbf24' : '#f59e0b';
    return mode === 'dark' ? '#60a5fa' : '#3b82f6'; // blue as default
  };

  const accentColor = getAccentColor(accent);

  return (
    <Dialog.Root open={isOpen}>
      <Dialog.Portal>
        <Dialog.Overlay 
          onClick={onSkip}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998] cursor-pointer"
          style={{ animation: 'fadeIn 0.2s ease-out' }}
        />
        <Dialog.Content 
          className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90vw] max-w-[650px] max-h-[85vh] overflow-y-auto rounded-xl shadow-2xl z-[9999] p-0 welcome-popup-scrollbar"
          style={{
            backgroundColor: mode === 'dark' ? '#1f2937' : '#ffffff',
            animation: 'slideIn 0.3s ease-out',
            border: `1px solid ${mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
          }}
        >
          {/* Header */}
          <div 
            className="px-6 py-5 border-b"
            style={{
              borderColor: mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
              background: mode === 'dark' 
                ? 'linear-gradient(135deg, rgba(31, 41, 55, 0.8) 0%, rgba(17, 24, 39, 0.9) 100%)'
                : 'linear-gradient(135deg, rgba(249, 250, 251, 1) 0%, rgba(243, 244, 246, 1) 100%)'
            }}
          >
            <Dialog.Title className="text-2xl font-bold flex items-center gap-3" style={{ color: 'var(--color-text)' }}>
              {isMobile ? 'Welcome to the new and improved 3M Navigator!' : (currentScreen === 1 ? 'Welcome to the new and improved 3M Navigator!' : 'Resources & Requirements')}
            </Dialog.Title>
            
            {/* Page Indicator - Desktop only */}
            {!isMobile && (
              <div className="flex gap-2 mt-3">
                <div 
                  className="h-1 flex-1 rounded-full transition-all"
                  style={{ backgroundColor: currentScreen === 1 ? accentColor : (mode === 'dark' ? 'rgba(75, 85, 99, 0.5)' : 'rgba(209, 213, 219, 1)') }}
                />
                <div 
                  className="h-1 flex-1 rounded-full transition-all"
                  style={{ backgroundColor: currentScreen === 2 ? accentColor : (mode === 'dark' ? 'rgba(75, 85, 99, 0.5)' : 'rgba(209, 213, 219, 1)') }}
                />
              </div>
            )}
          </div>

          {/* Mobile: All content on one page */}
          {isMobile && (
            <div className="px-6 py-6 space-y-6">
              {/* Introduction */}
              <Dialog.Description 
                className="text-base leading-relaxed"
                style={{ color: mode === 'dark' ? 'rgba(229, 231, 235, 0.9)' : 'rgba(31, 41, 55, 0.9)' }}
              >
                We've added powerful new features to help you get even more out of your 3M Navigator experience:
              </Dialog.Description>

              {/* Features List */}
              <div className="space-y-3">
                <div className="flex gap-3 items-start">
                  <span className="text-xl mt-0.5">🔧</span>
                  <div>
                    <strong style={{ color: 'var(--color-text)' }}>NEW Advanced Tools:</strong>
                    <span style={{ color: mode === 'dark' ? 'rgba(229, 231, 235, 0.8)' : 'rgba(55, 65, 81, 0.9)' }}>
                      {' '}Web Search, Reasoning, Image Generation, Data Analysis, Document Translation
                    </span>
                  </div>
                </div>

                <div className="flex gap-3 items-start">
                  <span className="text-xl mt-0.5">🤖</span>
                  <div>
                    <strong style={{ color: 'var(--color-text)' }}>AI Model Selection:</strong>
                    <span style={{ color: mode === 'dark' ? 'rgba(229, 231, 235, 0.8)' : 'rgba(55, 65, 81, 0.9)' }}>
                      {' '}Choose the model that best fits your preferences, between GPT and Claude.
                    </span>
                  </div>
                </div>

                <div className="flex gap-3 items-start">
                  <span className="text-xl mt-0.5">🎙️</span>
                  <div className="pt-1">
                    <strong style={{ color: 'var(--color-text)' }}>Voice Mode:</strong>
                    <span style={{ color: mode === 'dark' ? 'rgba(229, 231, 235, 0.8)' : 'rgba(55, 65, 81, 0.9)' }}>
                      {' '}Have natural conversations with 3M Navigator using your voice
                    </span>
                  </div>
                </div>

                <div className="flex gap-3 items-start">
                  <span className="text-xl mt-0.5">🎨</span>
                  <div>
                    <strong style={{ color: 'var(--color-text)' }}>New UI & Light Mode:</strong>
                    <span style={{ color: mode === 'dark' ? 'rgba(229, 231, 235, 0.8)' : 'rgba(55, 65, 81, 0.9)' }}>
                      {' '}Fresh look with light/dark themes and other customization options
                    </span>
                  </div>
                </div>

                <div className="flex gap-3 items-start">
                  <span className="text-xl mt-0.5">🏛️</span>
                  <div>
                    <strong style={{ color: 'var(--color-text)' }}>3M Knowledge:</strong>
                    <span style={{ color: mode === 'dark' ? 'rgba(229, 231, 235, 0.8)' : 'rgba(55, 65, 81, 0.9)' }}>
                      {' '}Specialized AI assistants backed by curated 3M content. Includes dedicated chats for Legal, DevOps, TEBG, Travel & Expenses, HR, Reporting Portal, and Everyday Insights, each grounded in trusted 3M sources.
                    </span>
                  </div>
                </div>
              </div>

              {/* Stay Updated Section */}
              <div
                className="rounded-lg p-4 space-y-2"
                style={{
                  backgroundColor: mode === 'dark' ? 'rgba(55, 65, 81, 0.3)' : 'rgba(16, 185, 129, 0.05)',
                  border: `1px solid ${mode === 'dark' ? 'rgba(75, 85, 99, 0.5)' : 'rgba(16, 185, 129, 0.2)'}`
                }}
              >
                <div className="flex items-center gap-2">
                  <span className="text-xl">📢</span>
                  <strong style={{ color: 'var(--color-text)' }}>Stay up to date</strong>
                </div>
                <p className="text-sm pl-1" style={{ color: mode === 'dark' ? 'rgba(229, 231, 235, 0.8)' : 'rgba(55, 65, 81, 0.9)' }}>
                  Check out our{' '}
                  <a
                    href="https://engage.cloud.microsoft/main/groups/eyJfdHlwZSI6Ikdyb3VwIiwiaWQiOiI0NTMyMzE3In0"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline font-medium"
                    style={{ color: accentColor }}
                  >
                    Viva Engage page
                  </a>
                  {' '}where we post all our updates
                </p>
              </div>

              {/* Prompt Engineering Section */}
              <div 
                className="rounded-lg p-4 space-y-3"
                style={{
                  backgroundColor: mode === 'dark' ? 'rgba(55, 65, 81, 0.3)' : 'rgba(59, 130, 246, 0.05)',
                  border: `1px solid ${mode === 'dark' ? 'rgba(75, 85, 99, 0.5)' : 'rgba(59, 130, 246, 0.15)'}`
                }}
              >
                <div className="flex items-center gap-2">
                  <span className="text-xl">💡</span>
                  <strong style={{ color: 'var(--color-text)' }}>Not getting the answers you want?</strong>
                </div>
                <p style={{ color: mode === 'dark' ? 'rgba(229, 231, 235, 0.8)' : 'rgba(55, 65, 81, 0.9)' }}>
                  Learn to prompt effectively with these resources:
                </p>
                <ul className="space-y-2 text-sm pl-1">
                  <li>
                    <a 
                      href="https://skydrive3m.sharepoint.com/teams/AI/SitePages/3M-Navigator-Prompt-Engineering-Office-Hours.aspx"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline flex items-center gap-2"
                      style={{ color: accentColor }}
                    >
                      <span className="hover:no-underline">📅</span> Prompt Engineering Office Hours
                    </a>
                  </li>
                  <li>
                    <a 
                      href="https://skydrive3m.sharepoint.com/teams/AI/SitePages/Prompt-Ideas.aspx?web=1"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline flex items-center gap-2"
                    >
                      <span className="hover:no-underline">📝</span> Writing Effective Prompts Guide
                    </a>
                  </li>
                  {/* TODO: PROMPT LIBRARY: ADD IN FOR R7 */}
                  {/* <li>
                    <a 
                      href="https://skydrive3m.sharepoint.com/teams/AI/SitePages/Prompt-Engineering.aspx?web=1"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline flex items-center gap-2"
                    >
                      <span>📚</span> Prompt Library from other 3Mers
                    </a>
                  </li> */}
                </ul>
              </div>
            </div>
          )}

          {/* Desktop: Content - Screen 1 */}
          {!isMobile && currentScreen === 1 && (
            <div className="px-6 py-6 space-y-8" style={{ minHeight: '420px' }}>
              {/* Introduction */}
              <Dialog.Description 
                className="text-base leading-relaxed"
                style={{ color: mode === 'dark' ? 'rgba(229, 231, 235, 0.9)' : 'rgba(31, 41, 55, 0.9)' }}
              >
                We've added powerful new features to help you get even more out of your 3M Navigator experience:
              </Dialog.Description>

              {/* Features List */}
              <div className="space-y-5">
                <div className="flex gap-3 items-start">
                  <span className="text-xl mt-0.5">🔧</span>
                  <div>
                    <strong style={{ color: 'var(--color-text)' }}>NEW Advanced Tools:</strong>
                    <span style={{ color: mode === 'dark' ? 'rgba(229, 231, 235, 0.8)' : 'rgba(55, 65, 81, 0.9)' }}>
                      {' '}Web Search, Reasoning, Image Generation, Data Analysis, Document Translation
                    </span>
                  </div>
                </div>

                <div className="flex gap-3 items-start">
                  <span className="text-xl mt-0.5">🤖</span>
                  <div>
                    <strong style={{ color: 'var(--color-text)' }}>AI Model Selection:</strong>
                    <span style={{ color: mode === 'dark' ? 'rgba(229, 231, 235, 0.8)' : 'rgba(55, 65, 81, 0.9)' }}>
                      {' '}Choose the model that best fits your preferences, between GPT and Claude.
                    </span>
                  </div>
                </div>

                <div className="flex gap-3 items-start">
                  <span className="text-xl mt-0.5">🎙️</span>
                  <div className="pt-1">
                    <strong style={{ color: 'var(--color-text)' }}>Voice Mode:</strong>
                    <span style={{ color: mode === 'dark' ? 'rgba(229, 231, 235, 0.8)' : 'rgba(55, 65, 81, 0.9)' }}>
                      {' '}Have natural conversations with 3M Navigator using your voice
                    </span>
                  </div>
                </div>

                <div className="flex gap-3 items-start">
                  <span className="text-xl mt-0.5">🎨</span>
                  <div>
                    <strong style={{ color: 'var(--color-text)' }}>New UI & Light Mode:</strong>
                    <span style={{ color: mode === 'dark' ? 'rgba(229, 231, 235, 0.8)' : 'rgba(55, 65, 81, 0.9)' }}>
                      {' '}Fresh look with light/dark themes and other customization options
                    </span>
                  </div>
                </div>

                <div className="flex gap-3 items-start">
                  <span className="text-xl mt-0.5">🏛️</span>
                  <div>
                    <strong style={{ color: 'var(--color-text)' }}>3M Knowledge:</strong>
                    <span style={{ color: mode === 'dark' ? 'rgba(229, 231, 235, 0.8)' : 'rgba(55, 65, 81, 0.9)' }}>
                      {' '}Specialized AI assistants backed by curated 3M content. Includes dedicated chats for Legal, DevOps, TEBG, Travel & Expenses, HR, Reporting Portal,and Everyday Insights, each grounded in trusted 3M sources.
                    </span>
                  </div>
                </div>
              </div>

            </div>
          )}

          {/* Desktop: Content - Screen 2 */}
          {!isMobile && currentScreen === 2 && (
            <div className="px-6 py-6 space-y-6" style={{ minHeight: '420px' }}>
              {/* Prompt Engineering Section */}
              <div 
                className="rounded-lg p-4 space-y-3"
                style={{
                  backgroundColor: mode === 'dark' ? 'rgba(55, 65, 81, 0.3)' : 'rgba(59, 130, 246, 0.05)',
                  border: `1px solid ${mode === 'dark' ? 'rgba(75, 85, 99, 0.5)' : 'rgba(59, 130, 246, 0.15)'}`
                }}
              >
                <div className="flex items-center gap-2">
                  <span className="text-xl">💡</span>
                  <strong style={{ color: 'var(--color-text)' }}>Not getting the answers you want?</strong>
                </div>
                <p style={{ color: mode === 'dark' ? 'rgba(229, 231, 235, 0.8)' : 'rgba(55, 65, 81, 0.9)' }}>
                  Learn to prompt effectively with these resources:
                </p>
                <ul className="space-y-2 text-sm pl-1">
                  <li>
                    <a 
                      href="https://skydrive3m.sharepoint.com/teams/AI/SitePages/3M-Navigator-Prompt-Engineering-Office-Hours.aspx"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline flex items-center gap-2"
                    >
                      <span className="hover:no-underline">📅</span> Prompt Engineering Office Hours
                    </a>
                  </li>
                  <li>
                    <a 
                      href="https://skydrive3m.sharepoint.com/teams/AI/SitePages/Prompt-Ideas.aspx?web=1"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline flex items-center gap-2"
                    >
                      <span className="hover:no-underline">📝</span> Writing Effective Prompts Guide
                    </a>
                  </li>
                  {/* TODO: PROMPT LIBRARY: ADD IN FOR R7 */}
                  {/* <li>
                    <a 
                      href="https://skydrive3m.sharepoint.com/teams/AI/SitePages/Prompt-Engineering.aspx?web=1"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline flex items-center gap-2"
                    >
                      <span>📚</span> Prompt Library from other 3Mers
                    </a>
                  </li> */}
                </ul>
              </div>

              {/* Stay Updated Section */}
              <div
                className="rounded-lg p-4 space-y-2"
                style={{
                  backgroundColor: mode === 'dark' ? 'rgba(55, 65, 81, 0.3)' : 'rgba(16, 185, 129, 0.05)',
                  border: `1px solid ${mode === 'dark' ? 'rgba(75, 85, 99, 0.5)' : 'rgba(16, 185, 129, 0.2)'}`
                }}
              >
                <div className="flex items-center gap-2">
                  <span className="text-xl">📢</span>
                  <strong style={{ color: 'var(--color-text)' }}>Stay up to date</strong>
                </div>
                <p className="text-sm pl-1" style={{ color: mode === 'dark' ? 'rgba(229, 231, 235, 0.8)' : 'rgba(55, 65, 81, 0.9)' }}>
                  Check out our{' '}
                  <a
                    href="https://engage.cloud.microsoft/main/groups/eyJfdHlwZSI6Ikdyb3VwIiwiaWQiOiI0NTMyMzE3In0"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline font-medium"
                  >
                    Viva Engage page
                  </a>
                  {' '}where we post all our updates
                </p>
              </div>
            </div>
          )}

          {/* Footer Buttons */}
          <div 
            className="px-6 py-4 border-t flex gap-3 justify-between"
            style={{
              borderColor: mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
              background: mode === 'dark' ? 'rgba(17, 24, 39, 0.5)' : 'rgba(249, 250, 251, 0.8)'
            }}
          >
            <div>
              {!isMobile && currentScreen === 2 && (
                <button
                  onClick={() => setCurrentScreen(1)}
                  className="px-5 py-2.5 rounded-lg font-medium transition-all hover:scale-105 flex items-center gap-2"
                  style={{
                    backgroundColor: mode === 'dark' ? 'rgba(75, 85, 99, 0.8)' : 'rgba(229, 231, 235, 1)',
                    opacity: 0.85,
                    color: mode === 'dark' ? 'rgba(229, 231, 235, 1)' : 'rgba(31, 41, 55, 1)',
                    border: `1px solid ${mode === 'dark' ? 'rgba(107, 114, 128, 0.5)' : 'rgba(209, 213, 219, 1)'}`
                  }}
                >
                  <ChevronLeftIcon className="w-4 h-4" />
                  <span className="pt-0.5">Back</span>
                </button>
              )}
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={onSkip}
                className="px-5 py-2.5 rounded-lg font-medium transition-all hover:scale-105"
                style={{
                  backgroundColor: mode === 'dark' ? 'rgba(75, 85, 99, 0.8)' : 'rgba(229, 231, 235, 1)',
                  opacity: 0.85,
                  color: mode === 'dark' ? 'rgba(229, 231, 235, 1)' : 'rgba(31, 41, 55, 1)',
                  border: `1px solid ${mode === 'dark' ? 'rgba(107, 114, 128, 0.5)' : 'rgba(209, 213, 219, 1)'}`
                }}
              >
                <span className="pt-0.5">{currentPage === 'main' && !isPromptLibraryOpen && !statisticsPanelOpen ? 'Skip & Explore' : 'Close'}</span>
              </button>
              
              {/* Mobile: guided tour not available — show nothing extra */}
              {isMobile ? null : (
                /* Desktop: Show Next or Start Guided Tour based on screen */
                currentScreen === 1 ? (
                  <button
                    onClick={() => setCurrentScreen(2)}
                    className="px-5 py-2.5 rounded-lg font-medium transition-all hover:scale-105 flex items-center gap-2 shadow-lg"
                    style={{
                      backgroundColor: accentColor,
                      color: '#ffffff',
                      border: 'none',
                    }}
                  >
                    <span className="pt-0.5">Next</span>
                    <ChevronRightIcon className="w-4 h-4" />
                  </button>
                ) : (
                  currentPage === 'main' && !isPromptLibraryOpen && !statisticsPanelOpen && (
                    <button
                      onClick={onStartTour}
                      className="px-5 py-2.5 rounded-lg font-medium transition-all hover:scale-105 flex items-center gap-2 shadow-lg hover:opacity-100"
                      style={{
                        backgroundColor: accentColor,
                        color: '#ffffff',
                        border: 'none',
                      }}
                    >
                      <RocketIcon className="w-4 h-4" />
                      <span className="pt-0.5">Start Guided Tour</span>
                    </button>
                  )
                )
              )}
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translate(-50%, -48%) scale(0.96);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
        }
        
        /* Custom scrollbar styling for desktop */
        @media (min-width: 768px) {
          .welcome-popup-scrollbar::-webkit-scrollbar {
            width: 8px;
          }
          
          .welcome-popup-scrollbar::-webkit-scrollbar-track {
            background: ${mode === 'dark' ? 'rgba(17, 24, 39, 0.5)' : 'rgba(243, 244, 246, 1)'};
            border-radius: 10px;
          }
          
          .welcome-popup-scrollbar::-webkit-scrollbar-thumb {
            background: ${mode === 'dark' ? 'rgba(107, 114, 128, 0.8)' : 'rgba(156, 163, 175, 0.8)'};
            border-radius: 10px;
            transition: background 0.2s;
          }
          
          .welcome-popup-scrollbar::-webkit-scrollbar-thumb:hover {
            background: ${accentColor};
          }
        }
      `}</style>
    </Dialog.Root>
  );
}
